import { type ComputedArrivalDto, ETA_PROVIDER } from "@clipboard-health/contract-backend-main";
import {
  API_TYPES,
  type ShiftAuctionBidBody,
  shiftAuctionBidBodySchema,
  type ShiftAuctionBidResponse,
  shiftAuctionBidResponseSchema,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type ComputedArrivalData } from "@src/appV2/redesign/ArrivalTime/computeArrivalData";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

const SHIFT_AUCTION_BIDS_PATH = "/shift-auction-bids";
const SHIFT_AUCTION_BIDS_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}${SHIFT_AUCTION_BIDS_PATH}`;

interface CreateShiftAuctionBidRequest {
  shiftAuctionId: string;
  bidAmount: number;
  computedArrivalData: ComputedArrivalData | undefined;
}

function toProviderEnum(provider: ComputedArrivalData["provider"]): ComputedArrivalDto["provider"] {
  if (provider === "AWS_LOCATION") {
    return ETA_PROVIDER.AWS_LOCATION;
  }

  if (provider === "SHIFT_START") {
    return ETA_PROVIDER.SHIFT_START;
  }

  return ETA_PROVIDER.GOOGLE_MAPS;
}

function toComputedArrivalDto(
  computedArrivalData: ComputedArrivalData | undefined
): ComputedArrivalDto | undefined {
  if (!isDefined(computedArrivalData)) {
    return undefined;
  }

  return {
    provider: toProviderEnum(computedArrivalData.provider),
    targetArrivalTime: computedArrivalData.targetArrivalTime.toISOString(),
    gracePeriodInMins: computedArrivalData.gracePeriodInMinutes,
    durationInMinutes: computedArrivalData.durationInMinutes,
    distanceInMeters: computedArrivalData.distanceInMeters,
  };
}

export function useCreateShiftAuctionBid(
  options: UseMutationOptions<
    ShiftAuctionBidResponse,
    AxiosError,
    CreateShiftAuctionBidRequest
  > = {}
) {
  return useMutation({
    mutationFn: async ({
      shiftAuctionId,
      bidAmount,
      computedArrivalData,
    }: CreateShiftAuctionBidRequest) => {
      const requestData: ShiftAuctionBidBody = {
        data: {
          type: API_TYPES.shiftAuctionBid,
          attributes: {
            bidAmount: {
              amountInMinorUnits: Math.round(bidAmount * 100), // Convert dollars to cents
              currencyCode: "USD" as const,
            },
            computedArrival: toComputedArrivalDto(computedArrivalData),
          },
          relationships: {
            shiftAuction: {
              data: {
                type: API_TYPES.shiftAuction,
                id: shiftAuctionId,
              },
            },
          },
        },
      };

      const response = await post({
        url: SHIFT_AUCTION_BIDS_URL,
        data: requestData,
        requestSchema: shiftAuctionBidBodySchema,
        responseSchema: shiftAuctionBidResponseSchema,
      });

      // Need a cast here because it otherwise does not resolve workerIdSchema correctly
      return response.data as ShiftAuctionBidResponse;
    },
    ...options,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_SHIFT_AUCTION_BID_FAILURE,
      ...options.meta,
    },
  });
}
